import pako from 'pako';
import { isApiError, ServiceError } from './error.js';
import { fetchData } from './fetch-data.js';
import { OAuthSession } from './oauth-session.js';

export type CreateRedirect = {
  source: string;
  destination: string;
  expiration_date?: string;
  status?: number;
  redirect_type: string;
  tag?: string;
};

export type Redirect = {
  id: number;
  source: string;
  destination: string;
  created_at: string;
  updated_at: string;
  expiration_date: string | undefined;
  redirect_type: string;
  status?: number;
  tag?: string;
};

export type Column = {
  label: string;
  sortable: boolean;
  field?: string;
  formatter?: (rawFieldContent: string | number) => unknown;
};

export type SortDirection = 'ASC' | 'DESC';

type ListParams = {
  sortBy?: string;
  sortDirection?: string;
  limit: number;
  offset?: number;
  search?: string;
  tag?: string;
};

export class RedirectService {
  public async bulkInsert(redirects: CreateRedirect[], updateExistingEntries = false) {
    const username = OAuthSession.getUserName();

    if (!username) {
      throw new Error('Redirects können nicht erstellt werden. Unbekannter Nutzer');
    }

    const compressedBody = pako.gzip(
      JSON.stringify({ updateExistingEntries, redirects, username })
    );

    const response = await fetchData('/api/redirects', {
      method: 'POST',
      headers: {
        'Content-Encoding': 'gzip',
        'Content-Type': 'application/json'
      },
      body: compressedBody
    });

    const responseData = await response.json();

    if (response.status === 406) {
      throw new ServiceError(406, JSON.stringify(responseData));
    } else if (response.status < 200 || response.status >= 400) {
      throw new Error(JSON.stringify(responseData));
    }

    return { responseData };
  }

  public async createRedirect(newRedirect: CreateRedirect) {
    const username = OAuthSession.getUserName();

    if (!username) {
      throw new Error('Redirect kann nicht erstellt werden. Unbekannter Nutzer');
    }

    const response = await fetchData('/api/redirects', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ...newRedirect, username })
    });

    if (response.status > 400) {
      const error = JSON.parse(await response.json());
      if (isApiError(error)) {
        if (error.code === 20) {
          throw new Error(`Es existiert bereits ein Redirect für '${error.message}'`);
        }
        if (error.code === 30) {
          const message = error.message.includes(',')
            ? `Die folgenden Werte sind ungültig: '${error.message}'`
            : `Der folgende Wert ist ungültig: '${error.message}'`;
          throw new Error(message);
        }
      }

      throw new Error('Es ist ein Fehler beim Erstellen des Redirects aufgetreten.');
    }
  }

  public async updateRedirect(changedRedirect: Redirect) {
    const username = OAuthSession.getUserName();

    if (!username) {
      throw new Error('Redirect kann nicht aktualisiert werden. Unbekannter Nutzer');
    }

    const response = await fetchData(`/api/redirects/${changedRedirect.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ...changedRedirect, username })
    });

    if (response.status >= 400) {
      const error = JSON.parse(await response.json());
      if (isApiError(error)) {
        if (error.code === 20) {
          throw new Error(`Es existiert bereits ein Redirect für '${error.message}'`);
        }
        if (error.code === 30) {
          const message = error.message.includes(',')
            ? `Die folgenden Werte sind ungültig: '${error.message}'`
            : `Der folgende Wert ist ungültig: '${error.message}'`;
          throw new Error(message);
        }
      }

      throw new Error(await response.json());
    }
  }

  public async delete(redirectId: number) {
    const username = OAuthSession.getUserName();

    if (!username) {
      throw new Error('Redirect kann nicht gelöscht werden. Unbekannter Nutzer');
    }

    const response = await fetchData(`/api/redirects/${redirectId}`, {
      method: 'DELETE',
      body: JSON.stringify({ username })
    });

    if (response.status < 200 || response.status >= 400) {
      throw new Error(
        `Es ist ein Fehler beim Löschen des Redirects mit der ID ${redirectId} aufgetreten.`
      );
    }
  }

  public async getList(params?: ListParams) {
    const searchParams = params
      ? `?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`
      : '';

    const response = await fetchData(`/api/redirects${searchParams}`);
    if (response.status >= 200 && response.status < 400) {
      return await response.json();
    }

    throw new Error('Es ist ein Fehler beim Laden der Redirects aufgetreten.');
  }

  public async getLogs(redirectId: number) {
    const response = await fetchData(`/api/redirects/${redirectId}/logs`);

    if (response.status >= 200 || response.status < 400) {
      return await response.json();
    }

    throw new Error(
      `Es ist ein Fehler beim Laden der Logs zum Redirect mit ID ${redirectId} aufgetreten.`
    );
  }

  public async validate(redirectId: number) {
    const response = await fetchData(`/api/redirects/${redirectId}/status`, {
      method: 'PATCH'
    });

    if (response.status >= 200 || response.status < 400) {
      return await response.json();
    }

    throw new Error(
      `Es ist ein Fehler beim validieren des Redirects mit der ID ${redirectId} aufgetreten.`
    );
  }
}
